import React, { useState, useEffect } from "react";
import {
getAuth,
signInWithEmailAndPassword,
sendPasswordResetEmail,
createUserWithEmailAndPassword,
signInWithRedirect,
GoogleAuthProvider,
sendSignInLinkToEmail,
isSignInWithEmailLink,
signInWithEmailLink,
getRedirectResult,
signOut,
updateProfile,
} from "firebase/auth";
import app from "../hooks/firebase";
import { useNavigate } from "react-router-dom";

const LoginHandler: React.FC = () => {
const auth = getAuth(app);
const navigate = useNavigate();
const [labels, setLabels] = useState<any>({
login: "Sign in",
signup: "Sign up",
forgot: "Forgot password?",
magiclink: "Sign in via Magic link",
});
const [user, setUser] = useState(auth.currentUser);
const [email, setEmail] = useState<string>("");
const [password, setPassword] = useState<string>("");
const [name, setName] = useState<string>("");
const [loading, setLoading] = useState<boolean>(false);
const [state, setState] = useState<
"login" | "signup" | "forgot" | "magiclink" | "google"
>("login");
const [toast, setToast] = useState<string>("");
const googleProvider = new GoogleAuthProvider();

const errorMessages: { [key: string]: string } = {
"auth/user-not-found": "No user found with this email.",
"auth/wrong-password": "Incorrect password. Please try again.",
"auth/email-already-in-use": "This email is already in use.",
"auth/invalid-email": "Please enter a valid email address.",
"auth/weak-password": "Password should be at least 6 characters long.",
"auth/operation-not-allowed": "This operation is not allowed.",
"auth/requires-recent-login":
"This operation requires you to log in again.",
default: "An unexpected error occurred. Please try again later.",
};

const sendToast = (content: string, timer = 3000) => {
setToast(content);
setTimeout(() => setToast(""), timer);
};

const handleError = (error: any) => {
const errorMessage =
errorMessages[error.code] || errorMessages.default;
sendToast(errorMessage);
};

const handleLogin = async () => {
try {
setLoading(true);
if (email.length < 1 || password.length < 1)
return sendToast("Please provide your email and password!");
await new Promise((resolve) => setTimeout(resolve, 700));
await signInWithEmailAndPassword(auth, email, password);
sendToast("Login Successful!");
} catch (error: any) {
handleError(error);
} finally {
setLoading(false);
}
};

const handleSignup = async () => {
try {
setLoading(true);
if (email.length < 1 || name.length < 1 || password.length < 1)
return sendToast("All fields are required!");
await new Promise((resolve) => setTimeout(resolve, 700));
const userCredential = await createUserWithEmailAndPassword(
auth,
email,
password
);
if (userCredential.user) {
await updateProfile(userCredential.user, { displayName: name });
sendToast("Account Created Successfully!");
setUser(auth.currentUser);
}
} catch (error: any) {
handleError(error);
} finally {
setLoading(false);
}
};

const handlePasswordReset = async () => {
try {
setLoading(true);
if (email.length < 1) return sendToast("Please provide your email!");
await new Promise((resolve) => setTimeout(resolve, 700));
await sendPasswordResetEmail(auth, email);
sendToast("Password reset email sent.");
} catch (error: any) {
handleError(error);
} finally {
setLoading(false);
}
};

const handleMagicLink = async () => {
try {
setLoading(true);
if (email.length < 1) return sendToast("Please provide your email!");
await new Promise((resolve) => setTimeout(resolve, 700));
const actionCodeSettings = {
url: window.location.href,
handleCodeInApp: true,
};
await sendSignInLinkToEmail(auth, email, actionCodeSettings);
localStorage.setItem("emailForSignIn", email);
sendToast("Magic link sent to your email!");
} catch (error: any) {
handleError(error);
} finally {
setLoading(false);
}
};

const handleGoogleLogin = async () => {
try {
setLoading(true);
await signInWithRedirect(auth, googleProvider);
} catch (error: any) {
handleError(error);
} finally {
setLoading(false);
}
};

const handleLogout = async () => {
try {
await signOut(auth);
setUser(null);
sendToast("Logged Out Successfully!");
} catch (error: any) {
handleError(error);
}
};

useEffect(() => {
document.title = "Sign in | LensTalk";

const unsubscribe = auth.onAuthStateChanged((currentUser) => {
setUser(currentUser);
});

const handleMagicLinkSignIn = async () => {
if (isSignInWithEmailLink(auth, window.location.href)) {
const storedEmail = localStorage.getItem("emailForSignIn");
if (!storedEmail) return sendToast("No email found for Magic Link.");
try {
setLoading(true);
await signInWithEmailLink(auth, storedEmail, window.location.href);
sendToast("Magic Link Login Successful!");
setUser(auth.currentUser);
localStorage.removeItem("emailForSignIn");
} catch (error: any) {
handleError(error);
} finally {
setLoading(false);
}
}
};

const fetchRedirectResult = async () => {
try {
setLoading(true);
const result = await getRedirectResult(auth);
if (result?.user) {
sendToast("Google Login Successful!");
setUser(auth.currentUser);
}
} catch (error: any) {
handleError(error);
} finally {
setLoading(false);
}
};

handleMagicLinkSignIn();
fetchRedirectResult();

return () => unsubscribe();
}, [auth]);

if (user) {
return (
<div style={{ padding: "2rem", maxWidth: "400px", margin: "auto" }}>
<h2>Welcome, {user.displayName || "User"}!</h2>
<p>Email: {user.email}</p>
<button
onClick={() => navigate("/")}
style={{ marginBottom: "1rem", width: "100%" }}
>
Continue to LensTalk
</button>
<button onClick={handleLogout} style={{ width: "100%" }}>
Logout
</button>
</div>
);
}

return (
<div style={{ padding: "2rem", maxWidth: "400px", margin: "auto" }}>
<h1>{state === "signup" ? "Sign Up" : "Sign In"}</h1>
<div role="tablist" style={{ display: "flex", marginBottom: "1rem" }}>
{["login", "signup", "forgot", "magiclink"].map((tab) => (
<button
key={tab}
role="tab"
aria-selected={state === tab}
onClick={() => setState(tab as any)}
style={{
flex: 1,
padding: "0.5rem",
backgroundColor: state === tab ? "#007bff" : "#f0f0f0",
color: state === tab ? "white" : "black",
border: "none",
cursor: "pointer",
}}
>
{labels[tab]}
</button>
))}
</div>
<div>
<input
type="email"
placeholder="Email"
value={email}
onChange={(e) => setEmail(e.target.value)}
style={{ marginBottom: "1rem", width: "100%", padding: "0.5rem" }}
/>
{state === "signup" && (
<input
type="text"
placeholder="Name"
value={name}
onChange={(e) => setName(e.target.value)}
style={{ marginBottom: "1rem", width: "100%", padding: "0.5rem" }}
/>
)}
{(state === "login" || state === "signup") && (
<input
type="password"
placeholder="Password"
value={password}
onChange={(e) => setPassword(e.target.value)}
style={{ marginBottom: "1rem", width: "100%", padding: "0.5rem" }}
/>
)}
</div>
{state === "login" && (
<button onClick={handleLogin} disabled={loading}>
{loading ? "Logging in..." : "Login"}
</button>
)}
{state === "signup" && (
<button onClick={handleSignup} disabled={loading}>
{loading ? "Signing Up..." : "Sign Up"}
</button>
)}
{state === "forgot" && (
<button onClick={handlePasswordReset} disabled={loading}>
{loading ? "Sending..." : "Reset Password"}
</button>
)}
{state === "magiclink" && (
<button onClick={handleMagicLink} disabled={loading}>
{loading ? "Sending Magic Link..." : "Send Magic Link"}
</button>
)}
<button
onClick={handleGoogleLogin}
style={{ marginTop: "1rem", display: "block", width: "100%" }}
>
Login with Google
</button>
{loading && (
<p style={{ marginTop: "1rem", color: "red" }} role="alert">
Loading...
</p>
)}
{toast && (
<p
style={{ marginTop: "1rem", color: "green" }}
aria-live="polite"
aria-atomic="true"
role="alert"
>
{toast}
</p>
)}
</div>
);
};

export default LoginHandler;
